/**
 * Keyboard and screen reader behaviour for the shared popup shell,
 * `snippets/popup.liquid`. Alpine already opens and closes the popup and
 * records the result in `data-open`. This file adds what the markup cannot:
 * focus in on open, focus back on close, Escape to close, Tab held inside.
 *
 * Terms used below:
 * - Alpine: a small JavaScript library that adds behaviour through HTML
 *   attributes such as `x-data` and `@click`.
 *   https://alpinejs.dev/
 * - Focus trap: code that keeps Tab inside a dialog, so focus cannot reach the
 *   page behind it. The pattern is described in the dialog guide.
 *   https://www.w3.org/WAI/ARIA/apg/patterns/dialog-modal/
 *
 * The panel fades in over a `visibility` transition, and an unrendered element
 * is not a focusable area, so `focus()` does nothing until that transition
 * ends. This file waits for the transition, not for the next frame.
 * https://html.spec.whatwg.org/multipage/interaction.html#focusable-area
 */
(function () {
  const OPEN_EVENT = 'aura-open-dialog';

  // JS hooks, set in `snippets/popup.liquid`.
  const SHELL_SELECTOR = '[aura-popup]';
  const PANEL_SELECTOR = '[aura-popup-panel]';

  // What Tab can reach. Disabled and `tabindex="-1"` elements are left out.
  const FOCUSABLE_SELECTOR = [
    'a[href]',
    'button:not([disabled])',
    'input:not([disabled])',
    'select:not([disabled])',
    'textarea:not([disabled])',
    'video[controls]',
    'audio[controls]',
    '[tabindex]:not([tabindex="-1"])',
  ].join(', ');

  // Grace added to the CSS duration so the fallback timer never cuts a
  // transition short.
  const TRANSITION_MARGIN_MS = 50;

  const shells = document.querySelectorAll(SHELL_SELECTOR);

  // Do not proceed if the page renders no popup. There is nothing to wire.
  if (!shells.length) {
    return;
  }

  // The control the shopper clicked last. Safari does not focus a button when
  // it is clicked, so focus sits on `<body>` when a popup opens from a click.
  // This is the fallback opener that focus goes back to on close.
  // https://developer.mozilla.org/en-US/docs/Web/HTML/Reference/Elements/button#clicking_and_focus
  let lastClickedControl = null;

  // Read in the capture phase, so it runs before the click opens the popup.
  document.addEventListener(
    'click',
    (event) => {
      lastClickedControl = event.target.closest(FOCUSABLE_SELECTOR);
    },
    true,
  );

  /**
   * Reads whether a shell is currently open.
   *
   * @param {HTMLElement} shell The popup's outer element.
   * @returns {boolean} True while the popup is on screen.
   */
  const isOpen = (shell) => {
    const openState = shell.dataset.open;

    return openState === 'yes';
  };

  /**
   * Lists the elements inside a panel that Tab can reach right now.
   *
   * @param {HTMLElement} panel The popup's panel element.
   * @returns {HTMLElement[]} The reachable elements, in document order.
   */
  const readTabStops = (panel) => {
    const candidates = Array.from(panel.querySelectorAll(FOCUSABLE_SELECTOR));

    return candidates.filter((element) => {
      // `offsetParent` is null inside a `display: none` branch, such as a
      // collapsed row. Tab skips those, so the wrap below must too.
      const isRendered = element.offsetParent !== null;

      return isRendered;
    });
  };

  /**
   * Reads how long a shell takes to fade in, in milliseconds.
   *
   * @param {HTMLElement} shell The popup's outer element.
   * @returns {number} The CSS transition duration, or 0 when it has none.
   */
  const readTransitionMs = (shell) => {
    // Reported in seconds, as "0.35s", one value per transitioned property.
    // The shell transitions visibility only.
    const seconds = parseFloat(getComputedStyle(shell).transitionDuration);

    // Do not proceed if the shell declares no transition. `parseFloat` returns
    // NaN then, and NaN cannot be turned into a delay.
    if (!Number.isFinite(seconds)) {
      return 0;
    }

    return seconds * 1000;
  };

  /**
   * Runs a callback once the panel is rendered and can take focus.
   *
   * @param {HTMLElement} shell The popup's outer element.
   * @param {Function} callback What to run when the panel is focusable.
   * @returns {void}
   */
  const whenRendered = (shell, callback) => {
    const alreadyRendered = getComputedStyle(shell).visibility === 'visible';

    // Do not proceed if the panel already renders, as under reduced motion. The
    // callback runs now, because a transition that never starts never ends.
    if (alreadyRendered) {
      callback();
      return;
    }

    let settled = false;
    let fallbackTimer = null;

    // Runs the callback once, whichever signal arrives first.
    const finish = () => {
      // Do not proceed if the other signal already won. The panel must be
      // focused once, not twice.
      if (settled) {
        return;
      }

      settled = true;
      shell.removeEventListener('transitionend', onEnd);
      clearTimeout(fallbackTimer);
      callback();
    };

    const onEnd = (event) => {
      // Do not proceed if the event is not about visibility. The panel and
      // backdrop bubble their own opacity and transform events here, and only
      // visibility marks the panel becoming focusable.
      if (event.propertyName !== 'visibility') {
        return;
      }

      finish();
    };

    shell.addEventListener('transitionend', onEnd);

    const fallbackDelayMs = readTransitionMs(shell) + TRANSITION_MARGIN_MS;

    // `transitionend` can go missing, for example when the CSS changes
    // mid-flight. This fallback timer reads the same CSS, so the two cannot
    // drift.
    fallbackTimer = setTimeout(() => {
      const isRendered = getComputedStyle(shell).visibility === 'visible';

      // Do not proceed if the shell is still hidden. A tab that is not drawn
      // never advances the transition, so `transitionend` fires once drawing
      // resumes.
      if (!isRendered) {
        return;
      }

      finish();
    }, fallbackDelayMs);
  };

  /**
   * Wires one popup shell.
   *
   * @param {HTMLElement} shell The popup's outer element.
   * @returns {void}
   */
  const setUpShell = (shell) => {
    const panel = shell.querySelector(PANEL_SELECTOR);

    // Do not proceed if the shell has no panel. There is nothing to focus or
    // contain.
    if (!panel) {
      return;
    }

    // What opened the popup, so focus can go back to it on close.
    let opener = null;

    // True from the moment the popup opens until the panel takes focus.
    let waitingForRender = false;

    /**
     * Reads whether this popup should answer a key now. Focus can be inside the
     * popup, on the opener during the fade-in, or nowhere (on `<body>`) after a
     * focused button is disabled, such as a gallery arrow on the last image.
     * Any other place is a layer above the popup, such as the cart drawer.
     *
     * @returns {boolean} True when no layer above the popup holds focus.
     */
    const readPopupOwnsFocus = () => {
      const focusedElement = document.activeElement;
      const focusIsInsidePopup = shell.contains(focusedElement);
      const focusIsOnOpener = focusedElement === opener;
      const focusWasLost = focusedElement === document.body;

      return focusIsInsidePopup || focusIsOnOpener || focusWasLost;
    };

    // Escape and the early Tab are read on the document, not on the shell,
    // because focus is still on the opener for as long as the panel takes to
    // fade in.
    document.addEventListener('keydown', (event) => {
      // Do not proceed if this popup is closed. It owns no key then.
      if (!isOpen(shell)) {
        return;
      }

      // Do not proceed if a layer above this popup holds focus, such as the
      // cart drawer an offer opens. That layer owns the keys.
      if (!readPopupOwnsFocus()) {
        return;
      }

      if (event.key === 'Tab') {
        // Do not proceed if the panel already has focus. The shell's own Tab
        // handler below takes over then.
        if (!waitingForRender) {
          return;
        }

        // Tab now would land on the page behind the backdrop. Drop it. The
        // panel takes focus within a moment, and the shopper can press Tab again.
        event.preventDefault();
        return;
      }

      // Do not proceed if the key is not Escape. Other keys are not this
      // handler's.
      if (event.key !== 'Escape') {
        return;
      }

      // An empty payload matches no `dialog_id`, so every popup closes through
      // the shell's own contract instead of its Alpine state.
      window.dispatchEvent(new CustomEvent(OPEN_EVENT, { detail: '' }));
    });

    // Tab inside the popup is read on the shell, so a layer opening above the
    // popup keeps the keyboard to itself. The cart drawer sets its own focus
    // trap on the document, and two focus traps would fight over every press.
    shell.addEventListener('keydown', (event) => {
      // Do not proceed if this popup is closed. It owns no key then.
      if (!isOpen(shell)) {
        return;
      }

      // Do not proceed if the key is not Tab. Other keys are not this
      // handler's.
      if (event.key !== 'Tab') {
        return;
      }

      const stops = readTabStops(panel);

      // Do not proceed if the panel has no tab stop. The shopper would have
      // nowhere to go.
      if (!stops.length) {
        return;
      }

      const first = stops[0];
      const last = stops[stops.length - 1];
      const focusedElement = document.activeElement;
      const onFirstStop = focusedElement === first;
      const onPanel = focusedElement === panel;

      // The panel holds focus until the shopper presses Tab, and it sits ahead
      // of every stop, so going back from the panel is going back from `first`.
      const atStart = onFirstStop || onPanel;
      const atEnd = focusedElement === last;

      // Plain Tab walks forward, so it leaves at the last stop and wraps to the
      // first one.
      let leavingThePanel = atEnd;
      let wrapTarget = first;

      // Shift+Tab walks backward, so both the edge and the target flip.
      if (event.shiftKey) {
        leavingThePanel = atStart;
        wrapTarget = last;
      }

      // Do not proceed while Tab stays inside the panel. The browser already
      // moves focus correctly there.
      if (!leavingThePanel) {
        return;
      }

      // Tab past an edge lands on the page behind the backdrop, which the
      // shopper cannot see. Send it to the opposite edge.
      event.preventDefault();
      wrapTarget.focus();
    });

    // Last seen state, so a rewrite of the same value is not read as a change.
    let wasOpen = isOpen(shell);

    // `data-open` is the shell's own record, so this works however the popup
    // was opened: by a trigger, by the window event or by the theme editor.
    const observer = new MutationObserver(() => {
      const openNow = isOpen(shell);
      const stateChanged = openNow !== wasOpen;

      // Do not proceed if the open state did not change. One open event reaches
      // every popup, and the others rewrite "no" over "no", so acting on them
      // would let a closed popup steal focus from the one that just opened.
      if (!stateChanged) {
        return;
      }

      wasOpen = openNow;

      if (openNow) {
        const focusWasLost = document.activeElement === document.body;

        opener = focusWasLost ? lastClickedControl : document.activeElement;
        waitingForRender = true;

        whenRendered(shell, () => {
          waitingForRender = false;

          // Do not proceed if the shopper closed the popup during the fade-in.
          // There is nothing to focus.
          if (!isOpen(shell)) {
            return;
          }

          panel.focus();
        });

        return;
      }

      waitingForRender = false;

      // Do not proceed if no opener was remembered. Focus then stays put
      // instead of jumping to the top of the page.
      if (!opener) {
        return;
      }

      opener.focus();
      opener = null;
    });

    observer.observe(shell, { attributes: true, attributeFilter: ['data-open'] });
  };

  shells.forEach((shell) => {
    setUpShell(shell);
  });
})();
